/**
 * FromTableChooser — clickable FROM-item trigger that opens a table-selector
 * popover, allowing the user to swap the relation referenced by a FROM item
 * while preserving the existing alias.
 *
 * The trigger renders with `data-testid="vqe-from-item-text-{index}"` (same
 * testid as the prior read-only span) so existing tests and e2e specs continue
 * to locate the FROM item label. The popover itself carries
 * `data-testid="vqe-from-change-table-{index}"` for change-specific queries.
 *
 * Only mounts the TanStack Query subscription when editContext is supplied.
 * VQE-RO callers never reach this component (FromItemRow guards via editReady).
 */

import { useMemo } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '../../../../../components/ui/popover';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { changeTable } from '../../edit/edits/changeTable';
import { tableSuggestionKey, tableSuggestionLabel } from '../../edit/edits/fromItemHelpers';
import { useTableSuggestions } from '../../edit/useSchemaSuggestions';

export interface FromTableChooserProps {
  readonly index: number;
  readonly leadingText: string;
  readonly editContext: VqeEditContext;
  readonly structure: QueryStructure;
}

export function FromTableChooser({
  index,
  leadingText,
  editContext,
  structure,
}: FromTableChooserProps) {
  const { track } = useAnalytics();
  const { tables, isLoading, hasSnapshot } = useTableSuggestions({
    service: editContext.service,
    scope: editContext.scope,
    cacheKey: `${editContext.documentId}::${editContext.statementId}::change-from::${index}`,
  });

  const items = useMemo(
    () =>
      tables.map((t) => ({
        key: tableSuggestionKey(t),
        label: tableSuggestionLabel(t),
      })),
    [tables],
  );

  const lookup = useMemo(() => {
    const map = new Map<string, (typeof tables)[number]>();
    for (const t of tables) map.set(tableSuggestionKey(t), t);
    return map;
  }, [tables]);

  const onSelect = (key: string): void => {
    const table = lookup.get(key);
    if (!table) return;
    // Success analytics is OUTCOME-DEPENDENT: `commitEdit` no longer rejects on a
    // commit failure (P12 §4.2), so fire `vqe_change_table` only when the edit
    // actually reached the buffer — otherwise a failed change-table would report
    // as a success. A commit-pipeline failure records its own feedback toast
    // inside commitEdit; the `index` here is always in range (component prop) so
    // `changeTable`'s wrapper pre-validation never rejects.
    void changeTable(index, table, toCommitCtx(editContext), { structure }).then((outcome) => {
      if (outcome.status === 'applied') track('feature:vqe_change_table');
    });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex-1 truncate rounded px-0.5 font-mono text-xs text-left hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          data-testid={`vqe-from-item-text-${index}`}
          aria-label="Change table"
        >
          {leadingText}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 text-xs"
        data-testid={`vqe-from-change-table-${index}`}
        onFocusOutside={(e) => e.preventDefault()}
      >
        {isLoading ? (
          <p className="italic text-muted-foreground" data-testid={`vqe-from-change-table-${index}-loading`}>
            Loading suggestions…
          </p>
        ) : items.length > 0 ? (
          <ul className="flex flex-col gap-1" data-testid={`vqe-from-change-table-${index}-items`}>
            {items.map((item) => (
              <li key={item.key}>
                <button
                  type="button"
                  className="w-full rounded px-2 py-1 text-left hover:bg-muted"
                  data-testid={`vqe-from-change-table-${index}-item-${item.key}`}
                  onClick={() => onSelect(item.key)}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p
            className="italic text-muted-foreground"
            data-testid={`vqe-from-change-table-${index}-empty`}
          >
            {hasSnapshot
              ? 'No tables in scope. Refresh the schema or pick a database with tables.'
              : 'No schema cache yet — connect to a database to populate suggestions.'}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
